// LifecycleEditions — the "this is bigger than two ends of life" teaser.
// Sits near the bottom of the landing as the final strategic prompt before
// the interest form: Ageing + Kids are the LAUNCH editions, but the engine
// can carry any memory journey — only the tablet cover changes.
//
// The case-backs are rendered in pure SVG/CSS (no AI imagery — brand rule):
// Pets (clay + paw), Service (camo + stylised gold winged dagger), Marriage
// (cream + interlocked rings), Travel (teal + globe and flag pins), and a
// blank "create your own".
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ForgetMeNotIcon } from "../ForgetMeNotIcon";
import { FmnWatermark } from "../FmnWatermark";

const FADE = {
    initial: { opacity: 0, y: 22 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1] },
};

const GOLD = "#D4AF37";

function Paw({ size = 64, color = "#FFF8F0", opacity = 1 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 64 64" fill={color} opacity={opacity} aria-hidden>
            <ellipse cx="32" cy="40" rx="13" ry="11" />
            <ellipse cx="15" cy="27" rx="6" ry="8" transform="rotate(-18 15 27)" />
            <ellipse cx="26" cy="19" rx="6" ry="8.5" transform="rotate(-6 26 19)" />
            <ellipse cx="38" cy="19" rx="6" ry="8.5" transform="rotate(6 38 19)" />
            <ellipse cx="49" cy="27" rx="6" ry="8" transform="rotate(18 49 27)" />
        </svg>
    );
}

function Rings({ width = 110 }) {
    return (
        <svg width={width} viewBox="0 0 110 78" fill="none" aria-hidden>
            <circle cx="42" cy="42" r="26" stroke={GOLD} strokeWidth="6" />
            <circle cx="70" cy="38" r="26" stroke="#C8A24B" strokeWidth="6" />
            {/* tiny diamond on the second band */}
            <path d="M70 6 L77 13 L70 20 L63 13 Z" fill="#FFFFFF" stroke="#C8A24B" strokeWidth="2.5" />
        </svg>
    );
}

function GlobeFlags({ width = 110 }) {
    const pins = [
        { x: 38, y: 26, c: "#E25555" },
        { x: 72, y: 20, c: "#F4D068" },
        { x: 84, y: 52, c: "#5B8DEF" },
        { x: 30, y: 58, c: "#67B26F" },
    ];
    return (
        <svg width={width} viewBox="0 0 110 100" fill="none" aria-hidden>
            <circle cx="55" cy="52" r="38" stroke="#EAF4F4" strokeWidth="3.5" />
            <ellipse cx="55" cy="52" rx="17" ry="38" stroke="#EAF4F4" strokeWidth="2.5" />
            <path d="M17 52 H93 M22 33 H88 M22 71 H88" stroke="#EAF4F4" strokeWidth="2.5" />
            {pins.map((p, i) => (
                <g key={i}>
                    <line x1={p.x} y1={p.y} x2={p.x} y2={p.y - 12} stroke="#FFFFFF" strokeWidth="2.5" />
                    <path d={`M${p.x} ${p.y - 12} h10 l-3 3.5 3 3.5 h-10 Z`} fill={p.c} />
                    <circle cx={p.x} cy={p.y} r="2.6" fill="#FFFFFF" />
                </g>
            ))}
        </svg>
    );
}

function CamoPattern() {
    // Auscam-inspired blobs, kept soft so the dagger reads first.
    return (
        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice" viewBox="0 0 200 260" aria-hidden>
            <rect width="200" height="260" fill="#5B6443" />
            <path d="M-10 30 C 40 0, 70 40, 110 22 C 150 6, 180 30, 210 18 L 210 -10 L -10 -10 Z" fill="#3C4632" />
            <path d="M-10 110 C 30 84, 60 128, 100 104 C 138 84, 170 120, 210 98 L 210 150 C 170 170, 130 140, 90 158 C 50 174, 20 150, -10 162 Z" fill="#7A5C3E" opacity="0.85" />
            <path d="M-10 220 C 40 196, 80 240, 130 216 C 165 200, 190 224, 210 212 L 210 270 L -10 270 Z" fill="#3C4632" />
            <ellipse cx="40" cy="70" rx="26" ry="13" fill="#A89368" opacity="0.5" />
            <ellipse cx="150" cy="60" rx="30" ry="14" fill="#2C3326" opacity="0.6" />
            <ellipse cx="60" cy="190" rx="32" ry="14" fill="#A89368" opacity="0.4" />
            <ellipse cx="160" cy="180" rx="24" ry="12" fill="#2C3326" opacity="0.55" />
        </svg>
    );
}

function PawPattern() {
    // Scattered low-opacity paws — same treatment as the teddies on Kids.
    const paws = [
        { x: "8%", y: "8%", s: 26, r: -18 }, { x: "66%", y: "6%", s: 20, r: 14 },
        { x: "30%", y: "26%", s: 16, r: 8 }, { x: "78%", y: "34%", s: 24, r: -10 },
        { x: "10%", y: "52%", s: 20, r: 16 }, { x: "60%", y: "62%", s: 16, r: -6 },
        { x: "26%", y: "78%", s: 24, r: 10 }, { x: "76%", y: "82%", s: 18, r: -14 },
    ];
    return (
        <div className="absolute inset-0" aria-hidden>
            {paws.map((p, i) => (
                <div key={i} className="absolute" style={{ left: p.x, top: p.y, transform: `rotate(${p.r}deg)` }}>
                    <Paw size={p.s} opacity={0.18} />
                </div>
            ))}
        </div>
    );
}

function TabletBack({ edition, index }) {
    const { t } = useTranslation();
    return (
        <motion.div
            {...FADE}
            transition={{ ...FADE.transition, delay: 0.08 + index * 0.1 }}
            className="flex flex-col items-center"
            data-testid={`lifecycle-card-${edition.key}`}
        >
            <div
                className={`relative w-full aspect-[3/4.1] rounded-[1.6rem] overflow-hidden shadow-rem-soft ${edition.frame || ""}`}
                style={edition.bg ? { background: edition.bg } : undefined}
            >
                {edition.pattern}
                {/* camera island — it's the BACK of the case */}
                <div className={`absolute top-3 left-3 w-8 h-8 rounded-xl flex items-center justify-center ${edition.dark ? "bg-black/25 border border-white/10" : "bg-black/10 border border-black/10"}`}>
                    <div className={`w-3 h-3 rounded-full ${edition.dark ? "bg-black/50 ring-2 ring-white/20" : "bg-black/25 ring-2 ring-black/10"}`} />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">{edition.motif}</div>
            </div>
            <p className="mt-5 font-sans text-[11px] uppercase tracking-[0.24em] text-[#6B7DB8] font-semibold">
                {t(`lifecycle.${edition.key}_label`)}
            </p>
            <p className="mt-2 text-xs text-[#8A8786] text-center leading-relaxed max-w-[190px]">
                {t(`lifecycle.${edition.key}_blurb`)}
            </p>
            <p
                className="mt-2 text-[11px] italic text-[#A85D62] text-center leading-snug max-w-[190px]"
                data-testid={`lifecycle-charity-${edition.key}`}
            >
                {t(`lifecycle.${edition.key}_charity`)}
            </p>
        </motion.div>
    );
}

const EDITIONS = [
    {
        key: "service",
        dark: true,
        pattern: <CamoPattern />,
        // The real SASR hat badge (user-approved asset) — never a redrawn approximation.
        motif: (
            <img
                src="/brand/product/sasr-badge.png"
                alt="SASR badge"
                className="w-[46%] max-w-[120px] object-contain drop-shadow-[0_4px_10px_rgba(0,0,0,0.45)]"
                data-testid="lifecycle-sasr-badge"
            />
        ),
    },
    {
        key: "marriage",
        bg: "linear-gradient(160deg, #FBF6EA 0%, #F3EAD6 100%)",
        frame: "border border-[#E5D9BC]",
        motif: <Rings width={112} />,
    },
    {
        key: "travel",
        bg: "linear-gradient(160deg, #3A7079 0%, #2C5A62 100%)",
        dark: true,
        motif: <GlobeFlags width={116} />,
    },
    {
        key: "custom",
        bg: "#FDFBF7",
        frame: "border-2 border-dashed border-[#C9C2B4]",
        motif: <span className="font-serif text-7xl text-[#B9B2A3]" aria-hidden>?</span>,
    },
];

export function LifecycleEditions() {
    const { t } = useTranslation();
    return (
        <section
            className="relative py-32 px-6 bg-[#FAF7F2] overflow-hidden"
            data-testid="lifecycle-editions-section"
            id="lifecycle"
        >
            <FmnWatermark position="bottom-right" size={760} opacity={0.05} rotate={-12} />
            <div className="max-w-7xl mx-auto">
                <motion.div {...FADE} className="text-center max-w-3xl mx-auto mb-16">
                    <div className="flex justify-center mb-6">
                        <ForgetMeNotIcon size={44} />
                    </div>
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-5">
                        {t("lifecycle.eyebrow")}
                    </p>
                    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D2B2A] leading-[1.06] tracking-tight">
                        {t("lifecycle.headline_part1")}{" "}
                        <em className="italic text-[#4A5A95]">{t("lifecycle.headline_emphasis")}</em>
                    </h2>
                    <p className="mt-7 text-lg text-[#5C5A59] leading-relaxed font-light">
                        {t("lifecycle.lead")}
                    </p>
                </motion.div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6 lg:gap-8 max-w-6xl mx-auto items-start">
                    {EDITIONS.map((ed, i) => (
                        <TabletBack key={ed.key} edition={ed} index={i} />
                    ))}
                </div>

                <motion.div {...FADE} transition={{ ...FADE.transition, delay: 0.5 }} className="mt-16 text-center">
                    <p className="text-base text-[#8A8786] font-serif italic max-w-2xl mx-auto" data-testid="lifecycle-footnote">
                        {t("lifecycle.footnote")}
                    </p>
                    <a
                        href="#interest"
                        className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#6B7DB8] hover:bg-[#4A5A95] text-white font-medium text-sm transition-all hover:-translate-y-0.5"
                        data-testid="lifecycle-cta"
                    >
                        {t("lifecycle.cta")}
                    </a>
                </motion.div>
            </div>
        </section>
    );
}
